import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import React, { useState } from 'react';
import * as actions from '@actions';
import { css } from '@emotion/react';
import { Checkbox, Col, Form, Radio, Row } from 'antd';
import { ConfirmationModal, errorModal, SuccessModal } from 'vl-common/src/components/Modals';
import moment from 'moment-timezone';
import { Appointment } from 'vl-common/src/schemas/shared';

interface AvailabilityModalProps {
  open: boolean;
  setRequest: (value: boolean) => void;
  user: any;
  case_id?: number | null;
  apptType?: string;
  appointment?: Appointment;
  updateActiveList: any;
}

const AvailabilityModal = ({
  open,
  setRequest,
  user,
  case_id,
  appointment,
  apptType,
  updateActiveList
}: AvailabilityModalProps) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const [{ days, time_slots }] = useState(() => {
    const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const time_slots = ['08:00-10:00', '10:00-12:00', '12:00-14:00', '14:00-16:00', '16:00-18:00', '18:00-20:00'];
    const stdate = new Date();
    const days = [];
    let c = 1;
    for (let i = 0; i < 7; i += 1) {
      if ((stdate.getDay() + c) % 7 !== 0) {
        days.push(weekday[(stdate.getDay() + c) % 7]);
      }
      c += 1;
    }
    return { days, time_slots };
  });
  const [timeValues, setTimeValues] = useState('');
  const [typeAppointment, setTypeAppointment] = useState('');
  const [taskCreate, setTaskCreate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isConsultRebook = appointment?.clinician_type_code === 'CONSULT';

  const onChange = (checkedValues: any) => {
    setTimeValues(checkedValues);
  };

  const onTypeChange = (checkedValues: any) => {
    setTypeAppointment(checkedValues.target.value);
  };

  const onSubmitSuccess = () => {
    setIsLoading(false);
    setRequest(false);
    setTaskCreate(true);
  };

  const requestAppointment = async () => {
    setIsLoading(true);
    const taskNotes = `Consultant appt. Patient availability (specified in the patient's timezone, ${user?.timezone}) ${timeValues} They would prefer: ${typeAppointment}, Please arrange and book within 24 working hrs. Appointment SLA 72 working hrs. IMPORTANT: If unable to book within this timeline please contact the patient with an update.`;
    const res = await dispatch(
      actions.taskCreate({
        user_guid: user.user_guid,
        patient_guid: user.user_guid,
        case_id,
        task_type_code: 'BOOK-APPOINTMENT',
        description: taskNotes,
        hbs_task: 1,
        task_created: new Date(),
        book_appt_type: apptType,
        appt_type_code: typeAppointment
      })
    );
    const originalResponse = res.payload;
    if (originalResponse?.status === 409) {
      const responseJson = await originalResponse.json().catch(() => null);
      errorModal({
        title: 'Cannot request appointment',
        content: responseJson?.message || 'Error while booking appointment'
      });
      setIsLoading(false);
      return;
    }
    if (res) {
      onSubmitSuccess();
    }
  };

  const requestConsultRebook = async () => {
    setIsLoading(true);
    const appointmentDetails = appointment as Appointment;
    moment.tz.setDefault(user?.timezone);
    const appointmentTimestapm = moment(appointmentDetails.datetime).format('DD/MM/YYYY @ HH:mm');
    const taskNotes = `Consultant appt. Patient availability (specified in the patient's timezone, ${user?.timezone}) ${timeValues} They would prefer: ${typeAppointment}. Previous appointment details: Appointment type - ${appointmentDetails.appt_type_desc}; Case - ${appointmentDetails.case_title}; Call type - ${appointmentDetails.call_type_desc}; Date @ Time - ${appointmentTimestapm} (in the patient's timezone); Clinician - ${appointmentDetails.clinician}.`;
    const res = await dispatch(
      actions.taskCreate({
        user_guid: user.user_guid,
        patient_guid: user.user_guid,
        appt_id: appointmentDetails?.appointment_id,
        case_id: appointmentDetails?.case_id,
        task_type_code: 'CONSULT-REBOOK',
        description: taskNotes,
        hbs_task: 1,
        task_created: new Date(),
        book_appt_type: appointmentDetails?.appt_type_code,
        appt_type_code: typeAppointment
      })
    );
    if (res) {
      onSubmitSuccess();
    }
  };

  return (
    <>
      <ConfirmationModal
        title="Choose availability time"
        open={open}
        destroyOnHidden
        width={750}
        onCancel={() => {
          setTypeAppointment('');
          setTimeValues('');
          setRequest(false);
        }}
        okButtonProps={{
          onClick: (isConsultRebook
            ? requestConsultRebook
            : requestAppointment) as React.MouseEventHandler<HTMLElement>,
          disabled: !timeValues || !typeAppointment,
          loading: isLoading
        }}
        okText={`Request ${isConsultRebook ? 'Reschedule' : 'Appointment'}`}
        cancelButtonProps={{
          onClick: () => {
            setTypeAppointment('');
            setTimeValues('');
            setRequest(false);
          }
        }}
      >
        <Form layout="vertical" data-testid="availability-matrix">
          <Form.Item
            label={`1. Please try to provide as many slots as possible, specified in your local timezone, ${user?.timezone}.`}
          >
            <Checkbox.Group onChange={onChange}>
              <Row gutter={[4, 4]} style={{ marginTop: 16 }}>
                <Col span={1} offset={3} />
                {days.map((day) => (
                  <Col
                    span={3}
                    key={day}
                    css={css`
                      display: flex;
                      justify-content: center;
                      align-items: center;

                      @media (max-width: 768px) {
                        writing-mode: vertical-lr;
                      }
                    `}
                  >
                    {day}
                  </Col>
                ))}

                {time_slots.map((time) => (
                  <React.Fragment key={time}>
                    <Col span={4}>{time}</Col>
                    {days.map((name, i) => (
                      <Col
                        key={name}
                        span={3}
                        css={css`
                          display: flex;
                          justify-content: center;
                        `}
                      >
                        <Checkbox value={`${name}, ${time}`} />
                      </Col>
                    ))}
                  </React.Fragment>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item label={t('2. Would you like a video consultation or a phone call?')} required>
            <Radio.Group onChange={onTypeChange}>
              <Radio value="VIDEO">{t('Video consultation')}</Radio>
              <Radio value="AUDIO">{t('Phone call')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </ConfirmationModal>

      <SuccessModal
        open={taskCreate}
        title={`${isConsultRebook ? 'Reschedule appointment' : 'Appointment'} request has been sent.`}
        data-testid="appointment-request-sent"
        onCancel={() => {
          setTaskCreate(false);
          updateActiveList();
        }}
      />
    </>
  );
};

export default AvailabilityModal;
